'use client';

import { FC, memo, useMemo } from 'react';
import { Box, List, ListItem } from '@mui/material';
import { IBadgeDetails } from '@/store/accountStore';
import { Link } from '@/components/general/Link/Link';
import { MenuNotificationBadge } from '@/components/blocks/header/MenuNotificationBadge/MenuNotificationBadge';
import { IPhrases } from '@/types/types/page/pages';
import { usePropsPhrases } from '@/utils/phrases/usePropsPhrases';
import { useElementDimensionBySelector } from '@/hooks/helpers/useElementDimensionBySelector/useElementDimensionBySelector';
import { getMenuItems, getAuthMenuItems, getMobileHeaderStyles } from '../menuUtils';
import { useCurrentPath } from '@/hooks/general/useCurrentPath/useCurrentPath';
import { useAuthMenuItemsByAccount } from '../useAuthMenuItemsByAccount';

interface MobileMenuProps {
    phrases?: IPhrases;
    open: boolean;
    onItemClick: () => void;
    isLoggedIn: boolean;
    badgeDetails: string;
    blogUrl?: string;
    faqUrl?: string;
    contactUrl?: string;
    helpCentreUrl?: string;
}

export const MobileMenu: FC<MobileMenuProps> = ({
    phrases,
    open,
    onItemClick,
    isLoggedIn,
    badgeDetails,
    blogUrl,
    faqUrl,
    contactUrl,
    helpCentreUrl,
}) => {
    const { t } = usePropsPhrases(phrases);
    const { pathname } = useCurrentPath();

    const headerHeight = useElementDimensionBySelector({
        selector: 'header',
        dimension: 'height',
        observeResize: false,
    });

    const { listSX, listItemSX, activeListItemSX, boxItemSX } = useMemo(
        () => getMobileHeaderStyles(open, headerHeight),
        [open, headerHeight],
    );

    const menuItems = getMenuItems(blogUrl, faqUrl, contactUrl, helpCentreUrl);
    const authMenuItems = getAuthMenuItems(JSON.parse(badgeDetails) as IBadgeDetails);

    const { authMenuItemsByAccount } = useAuthMenuItemsByAccount({ authMenuItems });

    return (
        <Box sx={boxItemSX} className="mobileMenuBox">
            <nav>
                {!isLoggedIn && open && (
                    <List className="topNavigationMenu" sx={listSX}>
                        {menuItems.map((menuItem) => {
                            const isActive = isActiveMenu({
                                ...menuItem,
                                currentPath: pathname,
                            });
                            if (!menuItem.invisible) {
                                return (
                                    <Link
                                        href={menuItem.href}
                                        target={menuItem.openInNewTab ? '_blank' : '_self'}
                                        key={menuItem.phraseId}
                                        onClick={onItemClick}
                                        passHref
                                    >
                                        <ListItem
                                            sx={(theme) =>
                                                isActive
                                                    ? activeListItemSX(theme)
                                                    : listItemSX(theme)
                                            }
                                            className={isActive ? 'active' : ''}
                                        >
                                            {t(menuItem.phraseId)}
                                        </ListItem>
                                    </Link>
                                );
                            }
                        })}
                    </List>
                )}
                {isLoggedIn && open && (
                    <List className="topNavigationMenu" sx={listSX}>
                        {authMenuItemsByAccount.map((menuItem) => {
                            const isActive = isActiveMenu({
                                ...menuItem,
                                currentPath: pathname,
                            });
                            return (
                                <Link
                                    href={menuItem.href}
                                    key={menuItem.phraseId}
                                    onClick={onItemClick}
                                    passHref
                                >
                                    <ListItem
                                        sx={(theme) =>
                                            isActive ? activeListItemSX(theme) : listItemSX(theme)
                                        }
                                        className={`${isActive ? 'active' : ''} ${menuItem.className ?? ''}`}
                                    >
                                        {menuItem.icon}
                                        <MenuNotificationBadge data={menuItem.data}>
                                            {t(menuItem.phraseId)}
                                        </MenuNotificationBadge>
                                    </ListItem>
                                </Link>
                            );
                        })}
                    </List>
                )}
            </nav>
        </Box>
    );
};

export const MemoizedMobileMenu = memo(MobileMenu);

const isActiveMenu = ({
    href,
    basePath,
    currentPath,
}: {
    href: string;
    basePath?: string;
    currentPath: string;
}) => currentPath.startsWith(basePath || href);
